import { Box, Stack, Typography } from '@mui/material';
import { IconTile } from 'src/components/IconTile';
import { SettingsSectionRowProps } from './settings-section-row.types';

/**
 * One setting inside a SettingsSectionCard: optional icon tile + title +
 * secondary line + a caller-supplied trailing control. The row is
 * presentational only — the control owns its own state and handlers.
 *
 * The icon and control align to the title line (flex-start), so a multi-line
 * secondary text flows below them without re-centering either edge.
 *
 * The control lives in the same column as the text (right of it, next to the
 * icon), so with `mobileControlLayout="below"` it drops under the text on mobile
 * (xs) sharing the text's left edge — not indented under the leading icon — and
 * returns inline from `sm` up. Use it for wide controls (e.g. Behavior selects).
 */
export const SettingsSectionRow = ({
  icon,
  title,
  secondary,
  control,
  controlAlign = 'start',
  mobileControlLayout = 'inline',
}: SettingsSectionRowProps) => {
  const stackBelowOnMobile = mobileControlLayout === 'below';
  const controlSelf = controlAlign === 'center' ? 'center' : 'flex-start';

  return (
    <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ px: 2.25, py: 2 }}>
      {icon ? <IconTile>{icon}</IconTile> : null}
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: stackBelowOnMobile ? { xs: 'column', sm: 'row' } : 'row',
          alignItems: 'flex-start',
          gap: 1.5,
        }}
      >
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} color="custom.OV.sys.onSurface">
            {title}
          </Typography>
          {secondary ? (
            <Typography variant="body2" color="custom.OV.sys.onSurfaceVariant" sx={{ mt: 0.5 }}>
              {secondary}
            </Typography>
          ) : null}
        </Box>
        {control ? (
          <Box
            sx={{
              flexShrink: 0,
              alignSelf: stackBelowOnMobile ? { xs: 'flex-start', sm: controlSelf } : controlSelf,
            }}
          >
            {control}
          </Box>
        ) : null}
      </Box>
    </Stack>
  );
};

SettingsSectionRow.displayName = 'SettingsSectionRow';
